import { useEffect, useState } from "react";
import axios from "axios";
import { Play, Music, Star, Mic2, ArrowLeft, Globe2, Sparkles, Heart } from "lucide-react";
import StarGlow from "@/StarGlow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TilloPage() {
  const [eps, setEps] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [glowing, setGlowing] = useState(null);

  useEffect(() => {
    axios.get(`${API}/tillo/episodes`).then((r) => setEps(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#FFF8EC", color: "#4A3B2A" }} data-testid="tillo-page">
      <header className="px-6 py-5" style={{ background: "#FFEFD2" }}>
        <div className="max-w-[1100px] mx-auto flex items-center justify-between gap-3">
          <a href="/" className="flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm transition-transform hover:scale-[1.03]" style={{ background: "#3E6E64", color: "#FFFFFF" }} data-testid="tillo-back-to-storylo">
            <ArrowLeft className="w-4 h-4" /> Back to Storylo
          </a>
          <div className="text-xs font-semibold uppercase tracking-widest text-right" style={{ color: "#C98A2B" }}>Kids · Tillo the Storysaur</div>
        </div>
      </header>

      <main className="px-6 py-10 max-w-[1100px] mx-auto">
        <div className="flex flex-col md:flex-row items-center gap-8 mb-6">
          <img src="/tillo.png" alt="Tillo the Storysaur" className="w-52 md:w-64 drop-shadow-xl" data-testid="tillo-hero-art" />
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4" style={{ background: "#FFE3A3", color: "#9A6A14" }}>
              <Star className="w-3.5 h-3.5" /> Stories. Songs. Kindness.
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ color: "#3E6E64" }}>
              Tillo the Storysaur
            </h1>
            <p className="mt-2 text-lg font-semibold" style={{ color: "#C98A2B" }}>
              Little stories. Original songs. Big messages for little people.
            </p>
            <p className="mt-4 text-base leading-relaxed max-w-xl" data-testid="tillo-intro">
              Meet Tillo, a little Storysaur with a big heart and a magical golden star.
              Join Tillo on gentle adventures about friendship, kindness, courage and
              discovery. Every adventure ends with an original song, so when the story
              finishes, it's time to sing along!
            </p>
            <p className="mt-3 text-sm italic" style={{ color: "#8A7A5E" }}>
              “Every little voice can help something shine.”
            </p>
          </div>
        </div>

        <h2 className="text-lg font-bold mt-10 mb-4" style={{ color: "#3E6E64" }}>Tillo's Adventures</h2>

        {/* Multi-language banner — visitors arriving from partner sites
            (e.g. Pistachios in the Park) learn Tillo speaks their language. */}
        <div className="rounded-3xl px-6 py-5 mb-6 flex flex-col sm:flex-row items-center gap-4" style={{ background: "#E8F4EF", border: "3px solid #BFE0D6" }} data-testid="tillo-languages-banner">
          <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "#3E6E64" }}>
            <Globe2 className="w-6 h-6 text-white" />
          </div>
          <div className="text-center sm:text-left">
            <div className="font-display text-lg" style={{ color: "#3E6E64" }}>Tillo speaks your language!</div>
            <p className="text-sm mt-0.5 leading-relaxed">
              Every adventure can be watched in <strong>14 languages</strong> — Türkçe, Français, Deutsch, Español, Italiano, العربية, हिन्दी, Ελληνικά and more. Press <strong>“Watch in your language”</strong> on any story and pick yours — the song stays in English with subtitles in your language, so everyone can sing along.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6" data-testid="tillo-episodes">
          {eps.map((ep) => (
            <div key={ep.id} className="rounded-3xl overflow-hidden shadow-lg" style={{ background: "#FFFFFF", border: "3px solid #FFE3A3" }} data-testid={`tillo-episode-${ep.id}`}>
              {playing === ep.id && ep.video_url ? (
                <div className="relative">
                  <video src={`${process.env.REACT_APP_BACKEND_URL}${ep.video_url}`} controls autoPlay className="w-full aspect-video bg-black" onEnded={() => setGlowing(ep.id)} data-testid="tillo-episode-player" />
                  {glowing === ep.id && <StarGlow onClose={() => setGlowing(null)} />}
                </div>
              ) : (
                <div className="w-full aspect-video flex items-center justify-center" style={{ background: "#E8F4EF" }}>
                  {ep.thumbnail_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${ep.thumbnail_url}`} alt={ep.title} className="w-full h-full object-cover" />
                  ) : (
                    <img src="/tillo.png" alt="Tillo" className="h-4/5 opacity-90" />
                  )}
                </div>
              )}
              <div className="p-5">
                <h3 className="font-display text-xl" style={{ color: "#3E6E64" }}>{ep.title}</h3>
                <p className="text-sm mt-1.5 leading-relaxed">{ep.description}</p>
                {ep.song_title && (
                  <div className="flex items-center gap-2 mt-3 text-sm font-semibold" style={{ color: "#C98A2B" }}>
                    <Music className="w-4 h-4" /> Song: “{ep.song_title}”
                  </div>
                )}
                {ep.video_url ? (
                  <>
                    <button
                      onClick={() => setPlaying(ep.id)}
                      className="mt-4 w-full py-3 rounded-full text-white font-bold text-base flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
                      style={{ background: "#3E6E64" }}
                      data-testid={`tillo-play-${ep.id}`}
                    >
                      <Play className="w-5 h-5" /> Play Story
                    </button>
                    <a
                      href={`/kids/episode/${ep.id}`}
                      className="mt-2.5 w-full py-2.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
                      style={{ background: "#FFE3A3", color: "#9A6A14" }}
                      data-testid={`tillo-singalong-${ep.id}`}
                    >
                      <Mic2 className="w-4 h-4" /> Sing Along
                    </a>
                    {ep.video_slug && (
                      <a
                        href={`/s/${ep.video_slug}`}
                        className="mt-2.5 w-full py-2.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
                        style={{ background: "#E8F4EF", color: "#3E6E64", border: "2px solid #BFE0D6" }}
                        data-testid={`tillo-languages-${ep.id}`}
                      >
                        <Globe2 className="w-4 h-4" /> Watch in your language
                      </a>
                    )}
                  </>
                ) : (
                  <div className="mt-4 w-full py-3 rounded-full text-center font-bold text-sm" style={{ background: "#FFE3A3", color: "#9A6A14" }} data-testid={`tillo-coming-soon-${ep.id}`}>
                    ✨ Coming soon
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Coming soon — personalised video adventures */}
        <div className="rounded-3xl px-6 py-6 mt-10 flex flex-col sm:flex-row items-center gap-5" style={{ background: "#FFEFD2", border: "3px solid #FFE3A3" }} data-testid="tillo-personalised-teaser">
          <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0" style={{ background: "#3E6E64" }}>
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <div className="text-center sm:text-left flex-1">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-2" style={{ background: "#3E6E64", color: "#FFFFFF" }}>
              ✨ Coming soon
            </div>
            <div className="font-display text-xl" style={{ color: "#3E6E64" }}>A Place in the Story — personalised adventures</div>
            <p className="text-sm mt-1 leading-relaxed">
              Soon your child can join Tillo and his friends in a story of their own! A cartoon character based on a photo you supply gives your little one a special place in Tillo's world — a keepsake for birthdays, celebrations or simply a thoughtful surprise.
            </p>
          </div>
          <a
            href="/kids/about"
            className="px-5 py-3 rounded-full font-bold text-sm whitespace-nowrap transition-transform hover:scale-[1.03]"
            style={{ background: "#3E6E64", color: "#FFFFFF" }}
            data-testid="tillo-about-link"
          >
            Find out more
          </a>
        </div>

        {/* Our purpose link */}
        <div className="mt-8 text-center" data-testid="tillo-purpose-link-row">
          <a href="/kids/about" className="inline-flex items-center gap-2 font-bold text-sm underline underline-offset-4" style={{ color: "#C98A2B" }} data-testid="tillo-purpose-link">
            <Heart className="w-4 h-4" /> Our Purpose: Little Stories, Lasting Lessons — read why we created Tillo
          </a>
        </div>
      </main>

      <footer className="px-6 py-8 text-center text-xs" style={{ color: "#8A7A5E" }}>
        Tillo the Storysaur · part of <a href="/" className="underline">Storylo</a>
      </footer>
    </div>
  );
}
